/*
 * @format
 * @flow
 */
import React, { useState, useEffect } from 'react';
import { get, escapeRegExp } from 'lodash';
import Autosuggest from 'react-autosuggest';
import { RequiredValidation, Label, Row, Cell } from 'sarsaparilla';

import { useValidation } from '../hooks/useValidation';
import { EXIT_LOCATION_ID_PROP_PATH } from '../constants/propPaths';
import type { DataBindingType, GenericReservationType } from '../types';
import AutoSuggestionContainer from './AutoSuggestionContainer';

const renderSuggestion = suggestion => {
  return (
    <Row>
      <Cell width={7}>{suggestion.name}</Cell>
      <Cell width={5}>{suggestion.town}</Cell>
    </Row>
  );
};

const defaultProps = {
  exitLabel: 'Exit Point',
};

type PropTypes = {
  exitLabel: string,
  dataBindingsMap: DataBindingType,
  onFormValidate: Function,
  pActions: {
    updatePendingReservation: Function,
  },
  displayValidationError: boolean,
  reservation: GenericReservationType,
  entrancesExits: Object,
  inputRef: any,
}

export default function ExitPoint({
  reservation,
  entrancesExits,
  pActions,
  onFormValidate,
  dataBindingsMap,
  exitLabel,
  displayValidationError,
  inputRef,
}: PropTypes) {
  const [value, setValue] = useState('');
  const [selectedValueObj, setSelectedValueObj] = useState({});
  const [suggestions, setSuggestions] = useState([]);

  const exitValue = get(reservation, dataBindingsMap[EXIT_LOCATION_ID_PROP_PATH], '');

  let noFilterSuggestions = true;
  let isClickedOnAutosuggest = false;

  const inputId = 'input-takeout-point';
  const { isInvalid } = useValidation(
    {value: exitValue, id: inputId},
    `${exitLabel} is required.`,
    inputRef
  );

  const validationError = exitValue === '' && isInvalid || displayValidationError ? 'rec-error' : '';

  useEffect(() => {
    const initialSuggestions = get(entrancesExits, 'validExits', []);
    const exitLocation = initialSuggestions.find(e => e.id === exitValue);
    const newValue = exitLocation ? exitLocation.name : '';
    setValue(newValue);
  }, []);

  const onAutoSuggestChange = (event, input) => {
    setValue(input.newValue);
  };

  const keyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const onAutosuggestClick = () => {
    noFilterSuggestions = true;
  };

  const shouldRenderSuggestions = suggestedValue => {
    noFilterSuggestions = true;
    if (!selectedValueObj[suggestedValue]) {
      noFilterSuggestions = false;
    }
    return true;
  };

  const onSuggestionSelected = (_, { suggestionValue }) => {
    setSelectedValueObj({ ...selectedValueObj, [suggestionValue]: true });
  };

  const onSuggestionsClearRequested = () => setSuggestions([]);

  const onSuggestionsFetchRequested = ({ value: suggestionValue }) => {
    if (isClickedOnAutosuggest) {
      onSuggestionsClearRequested();
      isClickedOnAutosuggest = false;
      return;
    }

    let newSuggestions = get(entrancesExits, 'validExits', []);
    const textValue = noFilterSuggestions ? '' : escapeRegExp(suggestionValue);
    const matcher = new RegExp(textValue, 'i');
    newSuggestions = newSuggestions.filter(item => {
      return matcher.test(`${item.town} ${item.name}`);
    });
    setSuggestions(newSuggestions);
  };

  const getSuggestionValue = suggestion => {
    const { name } = suggestion;
    pActions.updatePendingReservation(
      reservation.id,
      dataBindingsMap[EXIT_LOCATION_ID_PROP_PATH],
      suggestion.id
    );
    isClickedOnAutosuggest = true;
    return name;
  };

  const renderSuggestionsContainer = _props => {
    return <AutoSuggestionContainer {..._props} launchpoint />;
  };

  const inputProps = {
    id: inputId,
    placeholder: 'Type to select from the list...',
    'aria-label': `autosuggest-input`,
    className: validationError,
    value,
    onChange: onAutoSuggestChange,
    onKeyPress: keyPress,
    onClick: onAutosuggestClick,
  };

  return (
    <>
      <Label isRequired htmlFor={inputId}>
        {exitLabel}
      </Label>
      <Autosuggest
        id="takeout-point-2"
        alwaysRenderSuggestions
        shouldRenderSuggestions={shouldRenderSuggestions}
        onSuggestionSelected={onSuggestionSelected}
        value={exitValue}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={inputProps}
      />
      <RequiredValidation
        value={exitValue}
        displayInSummary
        onValidated={(isInValid, error) => {
          return onFormValidate(
            isInValid,
            error,
            dataBindingsMap[EXIT_LOCATION_ID_PROP_PATH]
          );
        }}
        controlLabel={exitLabel}
      />
    </>
  );
}

ExitPoint.defaultProps = defaultProps;
